import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import styles from './CookieConsent.module.scss';

const STORAGE_KEY = 'cu_cookie_consent';
const CONSENT_VERSION = '1';

/**
 * Cookie / privacy consent banner (GDPR / UAE PDPL / India DPDP).
 * Shows once until the visitor makes a choice. The choice is stored locally so the banner
 * stays dismissed, and — for a logged-in customer — also logged server-side for our records.
 */
const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const decide = (granted: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ granted, version: CONSENT_VERSION, at: new Date().toISOString() }));
    } catch { /* private mode — banner will simply reappear next visit */ }
    // Best-effort server log for signed-in users; harmless (and ignored) for guests.
    apiService.recordConsent('cookies', granted, CONSENT_VERSION).catch(() => {});
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.banner} role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div className={styles.inner}>
        <div className={styles.copy}>
          <span className={styles.emoji} aria-hidden>🍪</span>
          <p className={styles.text}>
            We use cookies to keep you signed in, remember your preferences, and understand how the app is used.
            You can read more in our{' '}
            <Link to="/cookie-policy" className={styles.link}>Cookie Policy</Link> and{' '}
            <Link to="/privacy" className={styles.link}>Privacy Policy</Link>.
          </p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.reject} onClick={() => decide(false)}>Reject non-essential</button>
          <button type="button" className={styles.accept} onClick={() => decide(true)}>Accept all</button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
